import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getBidSettingApi, updateBidSettingApi } from "../../api/core";
import { filterCarServiceApi } from "../../api/car-service";
import { IBidSetting } from "../../interface/bid-setting";
import { ICarService } from "../../interface/car-service";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import { currencySymbol, moneyDisplay } from "../../utils/money";
import { calcMinBid, calcSuggestedPrice, EXAMPLE_TRIP } from "../../utils/pricing";
import useSnackbar from "../../utils/snackbar";

const roundingOptions = [
  { value: 1, label: "ไม่ปัด" },
  { value: 500, label: `500 ${currencySymbol}` },
  { value: 1000, label: `1,000 ${currencySymbol}` },
  { value: 5000, label: `5,000 ${currencySymbol}` },
];

const SettingBidding = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(false);
  const [setting, setSetting] = useState<IBidSetting>({
    offerExpirySec: 60,
    counterLimit: 1,
    stepAmount: 1000,
    roundingStep: 1000,
  });
  const [carServices, setCarServices] = useState<ICarService[]>([]);
  const [km, setKm] = useState(EXAMPLE_TRIP.km);
  const [minutes, setMinutes] = useState(EXAMPLE_TRIP.minutes);

  const showError = (e: any) => {
    let msg: string | undefined;
    if (typeof e?.response?.data?.message === "string") {
      msg = e?.response?.data?.message;
    }
    openSnackbar(msg || "Error: Something wrong.");
  };

  const getData = async () => {
    try {
      setLoading(true);
      const [settingRes, carRes] = await Promise.all([
        getBidSettingApi(),
        filterCarServiceApi({}),
      ]);
      setSetting(settingRes.data);
      setCarServices(carRes.data ?? []);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      showError(e);
    }
  };

  const updateSetting = async () => {
    try {
      setLoading(true);
      const { data } = await updateBidSettingApi({
        offerExpirySec: setting.offerExpirySec,
        counterLimit: setting.counterLimit,
        stepAmount: setting.stepAmount,
        roundingStep: setting.roundingStep,
      });
      setSetting(data);
      setLoading(false);
      openSnackbar("Successfuly");
    } catch (e: any) {
      setLoading(false);
      showError(e);
    }
  };

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-header">
            <h4 className="mb-0">กติกาการเสนอราคา</h4>
          </div>
          <div className="card-body">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateSetting();
              }}
            >
              <div className="form-group row">
                <label className="col-sm-4 col-form-label">เวลาหมดอายุของข้อเสนอ</label>
                <div className="col-sm-4">
                  <div className="input-group" style={{ maxWidth: 200 }}>
                    <input
                      type="number"
                      className="form-control"
                      required
                      min={10}
                      max={600}
                      step={1}
                      value={setting.offerExpirySec}
                      onChange={(e) => {
                        setSetting((prev) => ({ ...prev, offerExpirySec: +e.target.value }));
                      }}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">วินาที</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group row">
                <label className="col-sm-4 col-form-label">คนขับเสนอราคากลับ (Counter-Offer)</label>
                <div className="col-sm-6">
                  <div className="input-group" style={{ maxWidth: 200 }}>
                    <input
                      type="number"
                      className="form-control"
                      required
                      min={1}
                      step={1}
                      value={setting.counterLimit}
                      onChange={(e) => {
                        setSetting((prev) => ({ ...prev, counterLimit: +e.target.value }));
                      }}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">ครั้ง / order</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group row">
                <label className="col-sm-4 col-form-label">ขั้นการปรับราคาเมื่อกด + / − ในแอป</label>
                <div className="col-sm-4">
                  <div className="input-group" style={{ maxWidth: 200 }}>
                    <input
                      type="number"
                      className="form-control"
                      required
                      min={1}
                      step={1}
                      value={setting.stepAmount}
                      onChange={(e) => {
                        setSetting((prev) => ({ ...prev, stepAmount: +e.target.value }));
                      }}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">{currencySymbol}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group row">
                <label className="col-sm-4 col-form-label">ปัดราคาแนะนำขึ้นเป็นหลัก</label>
                <div className="col-sm-4">
                  <select
                    className="form-control"
                    style={{ maxWidth: 200 }}
                    value={setting.roundingStep}
                    onChange={(e) => {
                      setSetting((prev) => ({ ...prev, roundingStep: +e.target.value }));
                    }}
                  >
                    {roundingOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button type="submit" className="btn btn-primary">
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-header d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            <h4 className="mb-0">ราคาแนะนำของรถแต่ละประเภท</h4>
            <div className="ml-auto d-flex flex-wrap" style={{ gap: 8 }}>
              <div className="input-group input-group-sm" style={{ width: 140 }}>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  step={0.5}
                  value={km}
                  onChange={(e) => setKm(+e.target.value)}
                />
                <div className="input-group-append">
                  <span className="input-group-text">กม.</span>
                </div>
              </div>
              <div className="input-group input-group-sm" style={{ width: 140 }}>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  step={1}
                  value={minutes}
                  onChange={(e) => setMinutes(+e.target.value)}
                />
                <div className="input-group-append">
                  <span className="input-group-text">นาที</span>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>ประเภทรถ</th>
                    <th className="text-right">ราคาเริ่มต้น</th>
                    <th className="text-right">ค่าระยะทาง</th>
                    <th className="text-right">ค่าเวลา</th>
                    <th className="text-right">ราคาแนะนำ</th>
                    <th className="text-right">Min Bid</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {carServices.map((c) => {
                    const editLink = (
                      <Link
                        className="btn btn-outline-primary btn-sm"
                        to={`${process.env.PUBLIC_URL}/car-service/edit/${c.id}`}
                      >
                        แก้ไข
                      </Link>
                    );
                    if (c.serviceType === 1) {
                      return (
                        <tr key={c.id}>
                          <td className="font-weight-bold">{c.name}</td>
                          <td colSpan={5} className="text-muted">
                            คิดค่าบริการแบบมิเตอร์ ไม่มีราคาแนะนำ
                          </td>
                          <td className="text-right">{editLink}</td>
                        </tr>
                      );
                    }
                    const p = calcSuggestedPrice(c, km, minutes, setting.roundingStep);
                    return (
                      <tr key={c.id}>
                        <td className="font-weight-bold">{c.name}</td>
                        <td className="text-right">{moneyDisplay(c.startPrice)}</td>
                        <td className="text-right">{moneyDisplay(p.distanceAmount)}</td>
                        <td className="text-right">{moneyDisplay(p.timeAmount)}</td>
                        <td className="text-right text-primary font-weight-bold">
                          {moneyDisplay(p.suggestedPrice)}
                        </td>
                        <td className="text-right font-weight-bold">
                          {c.minBidPct ? (
                            <>
                              {moneyDisplay(calcMinBid(p.suggestedPrice, c.minBidPct))}{" "}
                              <small className="text-muted">({c.minBidPct}%)</small>
                            </>
                          ) : (
                            <small className="text-muted">ยังไม่ตั้ง</small>
                          )}
                        </td>
                        <td className="text-right">{editLink}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingBidding;
